import { useCallback, useEffect, useRef, useState } from 'react';
import {
    IconArrowNarrowRight,
    IconAdjustments,
    IconAbc,
    IconArrowBarToDown,
    IconArrowBarToUp,
    IconBorderAll,
    IconChevronDown,
    IconChevronUp,
    IconDroplet,
    IconDropletOff,
    IconGridDots,
    IconLayoutAlignBottom,
    IconLayoutAlignCenter,
    IconLayoutAlignLeft,
    IconLayoutAlignMiddle,
    IconLayoutAlignRight,
    IconLayoutAlignTop,
    IconLayoutBottombar,
    IconLayoutNavbar,
    IconChartBar,
    IconChartPie,
    IconFileImport,
    IconLetterA,
    IconLetterCase,
    IconListNumbers as IconLineNumbers,
    IconBookmark,
    IconAlignJustified,
    IconAccessible,
    IconColumns,
    IconBrowser,
    IconPlus,
    IconFileDescription,
    IconFileText,
    IconLayoutBoardSplit,
    IconListTree,
    IconFileHorizontal,
    IconHistory,
    IconNote,
    IconNotes,
    IconList,
    IconScreenshot,
    IconShadow,
    IconSparkles,
    IconTypography,
    IconPercentage,
    IconRotate2,
    IconTable,
    IconArrowsMove,
    IconCalendarEvent,
    IconCircle,
    IconCopy,
    IconHandStop,
    IconHighlight,
    IconLayoutColumns,
    IconLayoutList,
    IconLine,
    IconLink,
    IconListNumbers,
    IconAlphabetGreek,
    IconMessage2,
    IconMathFunction,
    IconMoodSmile,
    IconPencil,
    IconPhoto,
    IconPhotoEdit,
    IconPhotoPlus,
    IconRectangle,
    IconRotate,
    IconRotateClockwise,
    IconRuler2,
    IconSignature,
    IconSort09,
    IconStrikethrough,
    IconTextPlus,
    IconTextRecognition,
    IconTrash,
    IconUnderline,
    IconSquarePlus,
    IconWindowMaximize,
    IconX,
    IconZoomScan,
} from '@tabler/icons-react';
import {
    FOOTER_ROLE,
    HEADER_ROLE,
    OBJ,
    MARGIN_PRESETS,
    PAGE_BORDER_ROLE,
    PAGE_SIZES,
    PAGE_NUMBER_ROLE,
    TOOLS,
    WATERMARK_ROLE,
} from '../constants';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../utils/bnFormat';
import { findSelected, usePdfEditorStore } from '../state/usePdfEditorStore';
import Hint from '../../../components/common/Hint';
import {
    EDITOR_CHROME_ATTR,
    MenuItem,
    Popover,
    keepTextEditorFocus,
    useCompactLayout,
} from '../ui/prims';
import RibbonTextGroups from './RibbonTextGroups';
import { useFormatting } from './useFormatting';

/** The tick this stamps on a page; Tabler's own is too thin to read at 17px. */
function IconCheckMark({ size = 20, stroke = 1.7 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke * 1.2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 12.5 9.5 18 20 6.5" />
        </svg>
    );
}

/**
 * A tool drawn big: a large icon over a SHORT name.
 *
 * The full name ("Remove watermark") never fit under a 62px button and was cut
 * to "Remove wat…", so the caption is a one-word `short` and the whole name plus
 * a line on what the tool does lives in the hover tooltip instead.
 *
 * The caption's line-height is generous on purpose: Bengali stacks vowel signs
 * above and below the line, and `truncate` clips to the line box, so the old
 * 1.15 sliced the tops and tails off the letters.
 */
function BigTool({ icon: Icon, label, short, hint, active, disabled, onClick }) {
    const caption = short || label;
    const tip = hint
        ? (
            <span className="block text-left">
                <span className="block font-semibold">{label}</span>
                <span className="mt-0.5 block max-w-[230px] text-[11px] font-normal leading-snug opacity-80">{hint}</span>
            </span>
        )
        : label;
    return (
        <Hint label={tip}>
            <button
                type="button"
                onClick={onClick}
                disabled={disabled}
                aria-label={label}
                aria-pressed={!!active}
                className={`flex h-[74px] w-[70px] flex-none flex-col items-center justify-center gap-[4px] rounded-[10px] px-1 transition-colors disabled:pointer-events-none disabled:opacity-40 ${
                    active
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-[#3D3833] hover:bg-[#F0ECE8] dark:text-zinc-200 dark:hover:bg-zinc-800'
                }`}
            >
                <Icon size={34} stroke={1.6} />
                <span className="w-full truncate py-px text-center text-[11.5px] font-semibold leading-[1.5]">{caption}</span>
            </button>
        </Hint>
    );
}

/** A tool drawn small: the ones that read fine as a glyph alone. */
function SmallTool({ icon: Icon, label, active, disabled, onClick }) {
    return (
        <Hint label={label}>
            <button
                type="button"
                onClick={onClick}
                disabled={disabled}
                aria-label={label}
                aria-pressed={!!active}
                className={`flex h-9 w-9 flex-none items-center justify-center rounded-[9px] transition-colors disabled:pointer-events-none disabled:opacity-40 ${
                    active
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-[#3D3833] hover:bg-[#F0ECE8] dark:text-zinc-200 dark:hover:bg-zinc-800'
                }`}
            >
                <Icon size={24} stroke={1.7} />
            </button>
        </Hint>
    );
}

/**
 * One labelled column of the ribbon.
 *
 * The caption underneath is the whole point of the shape: a flat row of thirty
 * glyphs says nothing about which of them belong together, and this says it
 * once per group instead of once per button.
 */
function Group({ label, children, last = false }) {
    return (
        <div className={`flex flex-none flex-col px-3 ${last ? '' : 'border-r border-[#F0ECE8] dark:border-zinc-800'}`}>
            <div className="flex flex-1 items-center gap-[3px]">{children}</div>
            <div className="pb-[4px] pt-[2px] text-center text-[10.5px] font-semibold leading-[1.5] text-[#B8B1AA]">
                {label}
            </div>
        </div>
    );
}

/** Small tools stacked two to a row, for groups of four or more glyphs. */
function Stack({ children }) {
    return <div className="flex flex-col justify-center gap-[5px]">{children}</div>;
}

function Row({ children }) {
    return <div className="flex items-center gap-[3px]">{children}</div>;
}

/**
 * The editor's tool ribbon: five tabs, each a row of labelled groups.
 *
 * The flat bar this replaces had every tool in the editor on one line, sized by
 * a measuring ladder so it would fit — which it did, at the cost of thirty
 * unlabelled glyphs in a row. Tabs buy the room back: only the tools for one
 * kind of work are on screen, so each can be big enough to name itself, and the
 * groups say what the tools have in common.
 */
export default function Ribbon({
    onPickImage,
    onInsertDate,
    onInsertTable,
    onCoverPage,
    onChart,
    onScreenshot,
    onField,
    onDropCap,
    onCommand,
    onCheckAccessibility,
    onColumns,
    onWatermarkImage,
    removingWatermark,
    onInsertNote,
    onAddBookmark,
    // The tab row doubles as the editor's title bar: the document's name at
    // the left end, its actions and the window controls at the right.
    leading = null,
    trailing = null,
    // No document yet: the tabs and tools stay in view but cannot be used.
    // `leading`/`trailing` stay live — they hold the close button.
    disabled = false,
}) {
    const { t, currentLanguage } = useTranslation();
    // Counts that go into a label. Every other number the editor shows is set
    // in the reader's own digits, and a lone Latin "2" beside them reads as a
    // mistake.
    const num = useCallback(
        (value) => (currentLanguage === 'bn' ? toBnDigits(value) : String(value)),
        [currentLanguage],
    );
    const compact = useCompactLayout();
    const [tab, setTab] = useState('home');

    // The groups row scrolls sideways when a tab has more groups than the window is wide. Its
    // scrollbar is always shown (see .pdf-ribbon-scroll), and a plain mouse wheel — which only
    // knows up/down — scrolls along it, so the clipped groups are never out of reach.
    const groupsRef = useRef(null);
    useEffect(() => {
        const el = groupsRef.current;
        if (!el) return undefined;
        const onWheel = (e) => {
            if (el.scrollWidth <= el.clientWidth) return;
            if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
                el.scrollLeft += e.deltaY;
                e.preventDefault();
            }
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, []);
    // A different tab is a different row: start it from the left.
    useEffect(() => { if (groupsRef.current) groupsRef.current.scrollLeft = 0; }, [tab]);
    // The scrollbar takes 8px of the row, so the row only grows when it is actually overflowing.
    const [overflowing, setOverflowing] = useState(false);
    useEffect(() => {
        const el = groupsRef.current;
        if (!el) return undefined;
        const measure = () => {
            const next = el.scrollWidth > el.clientWidth + 1;
            setOverflowing((prev) => (prev === next ? prev : next));
        };
        measure();
        const resize = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        resize?.observe(el);
        // Groups come and go with the selection, which changes the width without resizing the row.
        const mutate = typeof MutationObserver !== 'undefined' ? new MutationObserver(measure) : null;
        mutate?.observe(el, { childList: true, subtree: true });
        return () => { resize?.disconnect(); mutate?.disconnect(); };
    }, [tab]);

    const activeTool = usePdfEditorStore((s) => s.activeTool);
    const setTool = usePdfEditorStore((s) => s.setTool);
    const documentMode = usePdfEditorStore((s) => s.documentMode);
    const clickToEditText = usePdfEditorStore((s) => s.clickToEditText);
    const showThumbnails = usePdfEditorStore((s) => s.showThumbnails);
    const toggleThumbnails = usePdfEditorStore((s) => s.toggleThumbnails);
    const showInspector = usePdfEditorStore((s) => s.showInspector);
    const toggleInspector = usePdfEditorStore((s) => s.toggleInspector);
    const setPagesModal = usePdfEditorStore((s) => s.setPagesModal);
    const setSignModal = usePdfEditorStore((s) => s.setSignModal);
    const setEmojiModal = usePdfEditorStore((s) => s.setEmojiModal);
    const setSymbolModal = usePdfEditorStore((s) => s.setSymbolModal);
    const setEquationModal = usePdfEditorStore((s) => s.setEquationModal);
    const setFitMode = usePdfEditorStore((s) => s.setFitMode);
    const fitMode = usePdfEditorStore((s) => s.fitMode);
    const viewMode = usePdfEditorStore((s) => s.viewMode);
    const setViewMode = usePdfEditorStore((s) => s.setViewMode);
    // Page numbering is on when its objects are on the page — read from the
    // document itself, so undo and redo move the button with them.
    const numbered = usePdfEditorStore((s) => Object.values(s.objectsByUid)
        .some((list) => list?.some((o) => o.role === PAGE_NUMBER_ROLE)));
    const numberDigits = usePdfEditorStore((s) => s.toolSettings.pageNumbers?.digits || 'bn');
    const updateToolSettings = usePdfEditorStore((s) => s.updateToolSettings);

    const shapeKind = usePdfEditorStore((s) => s.toolSettings.shape.kind);

    // Page operations act on the page you are looking at, and the clipboard
    // pair on the object you have picked, so both need to read them.
    const pages = usePdfEditorStore((s) => s.pages);
    const currentPage = usePdfEditorStore((s) => s.currentPage);
    const selectedId = usePdfEditorStore((s) => s.selectedId);
    const objectsByUid = usePdfEditorStore((s) => s.objectsByUid);
    const updateObject = usePdfEditorStore((s) => s.updateObject);
    const insertBlankPage = usePdfEditorStore((s) => s.insertBlankPage);
    const duplicatePages = usePdfEditorStore((s) => s.duplicatePages);
    const deletePages = usePdfEditorStore((s) => s.deletePages);
    const rotatePage = usePdfEditorStore((s) => s.rotatePage);
    const rotateAllPages = usePdfEditorStore((s) => s.rotateAllPages);
    const showRuler = usePdfEditorStore((s) => s.showRuler);
    const toggleRuler = usePdfEditorStore((s) => s.toggleRuler);

    const pageUid = pages[currentPage]?.uid;
    const onePage = pages.length < 2;
    const selection = findSelected({ selectedId, pages, objectsByUid });

    const numbersRef = useRef(null);
    const tableRef = useRef(null);
    const watermarkRef = useRef(null);
    const headerRef = useRef(null);
    const footerRef = useRef(null);
    const pageBorderRef = useRef(null);
    const chartRef = useRef(null);
    const coverRef = useRef(null);
    const fieldRef = useRef(null);
    const spacingRef = useRef(null);
    const marginsRef = useRef(null);
    const sizeRef = useRef(null);
    const columnsRef = useRef(null);
    const [cover, setCover] = useState({ title: '', subtitle: '', author: '' });
    const [chart, setChart] = useState({ kind: 'bar', data: '10, 24, 18, 32' });
    const [openMenu, setOpenMenu] = useState(null);
    const [stampText, setStampText] = useState('');

    const addObject = usePdfEditorStore((s) => s.addObject);
    const removeObject = usePdfEditorStore((s) => s.removeObject);
    const reorderObject = usePdfEditorStore((s) => s.reorderObject);
    const displayMode = usePdfEditorStore((s) => s.displayMode);
    const setDisplayMode = usePdfEditorStore((s) => s.setDisplayMode);
    const splitView = usePdfEditorStore((s) => s.splitView);
    const toggleSplitView = usePdfEditorStore((s) => s.toggleSplitView);
    const showGrid = usePdfEditorStore((s) => s.showGrid);
    const toggleGrid = usePdfEditorStore((s) => s.toggleGrid);
    const setZoom = usePdfEditorStore((s) => s.setZoom);
    const showAccessibility = usePdfEditorStore((s) => s.showAccessibility);
    const toggleAccessibility = usePdfEditorStore((s) => s.toggleAccessibility);
    const showBookmarks = usePdfEditorStore((s) => s.showBookmarks);
    const toggleBookmarks = usePdfEditorStore((s) => s.toggleBookmarks);
    const margins = usePdfEditorStore((s) => s.margins);
    const marginPreset = usePdfEditorStore((s) => s.marginPreset);
    const showMargins = usePdfEditorStore((s) => s.showMargins);
    const setMargins = usePdfEditorStore((s) => s.setMargins);
    const toggleMargins = usePdfEditorStore((s) => s.toggleMargins);
    const setPageSize = usePdfEditorStore((s) => s.setPageSize);
    const hyphenate = usePdfEditorStore((s) => !!s.toolSettings.text.hyphenate);
    const setHyphenation = usePdfEditorStore((s) => s.setHyphenation);
    const trackChanges = usePdfEditorStore((s) => s.trackChanges);
    const toggleTrackChanges = usePdfEditorStore((s) => s.toggleTrackChanges);
    const showReviewing = usePdfEditorStore((s) => s.showReviewing);
    const toggleReviewing = usePdfEditorStore((s) => s.toggleReviewing);

    // Which document-wide switches are currently on, read from the pages so
    // undo moves the buttons with it.
    const roleOn = usePdfEditorStore((s) => {
        const on = new Set();
        Object.values(s.objectsByUid).forEach((list) => list?.forEach((obj) => {
            if (obj.role) on.add(obj.role);
        }));
        return [...on].sort().join(',');
    });
    const isOn = (role) => roleOn.split(',').includes(role);

    const selectedObject = selection?.object || null;

    // Underline / strike-out on the ribbon do two jobs. With a text box selected they
    // format IT — the same as the inspector's U button — and with nothing (or anything
    // else) selected they arm the "mark a PDF line" tool. Only the first used to exist
    // here, which is why the panel's button worked and the ribbon's seemed dead.
    const { write: writeText, textSource } = useFormatting();
    const textSelected = selectedObject?.type === OBJ.TEXT;

    /* ------------------------------------------------------------- text */

    const changeCase = (mode) => {
        if (!selectedObject || selectedObject.type !== OBJ.TEXT) return;
        const text = selectedObject.text || '';
        const next = mode === 'upper'
            ? text.toUpperCase()
            : mode === 'lower'
                ? text.toLowerCase()
                // Title case: every word's first letter, which is what Word's
                // "Capitalize Each Word" does.
                : text.replace(/\S+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
        if (next !== text) updateObject(selection.uid, selectedObject.id, { text: next, marks: null });
    };

    const toggleStrikeThrough = () => {
        if (!selectedObject || selectedObject.type !== OBJ.TEXT) return;
        updateObject(selection.uid, selectedObject.id, { strike: !selectedObject.strike });
    };

    /* --------------------------------------------------------- arrange */

    /** Word aligns a floating object against the page; so does this. */
    const alignToPage = (how) => {
        const page = pages[currentPage];
        if (!selectedObject || !page) return;
        const patch = {
            left: { x: 0 },
            center: { x: (page.width - selectedObject.w) / 2 },
            right: { x: page.width - selectedObject.w },
            top: { y: 0 },
            middle: { y: (page.height - selectedObject.h) / 2 },
            bottom: { y: page.height - selectedObject.h },
        }[how];
        if (patch) updateObject(selection.uid, selectedObject.id, patch);
    };

    const rotateSelection = (delta) => {
        if (!selectedObject) return;
        const next = (((selectedObject.rotation || 0) + delta) % 360 + 360) % 360;
        updateObject(selection.uid, selectedObject.id, { rotation: next });
    };

    const order = (direction) => {
        if (!selectedObject) return;
        reorderObject(selection.uid, selectedObject.id, direction);
    };

    // Pressing the tool you are already holding puts the arrow back, which is
    // how the flat bar behaved and the only way out of a tool by mouse.
    const tool = (kind) => ({
        active: activeTool === kind,
        onClick: () => setTool(activeTool === kind ? TOOLS.SELECT : kind),
    });

    // A shape is the shape tool plus which shape; the tool stays put while the
    // kind changes under it.
    const shape = (kind) => ({
        active: activeTool === TOOLS.SHAPE && shapeKind === kind,
        onClick: () => {
            updateToolSettings('shape', { kind });
            setTool(TOOLS.SHAPE);
        },
    });

    const TABS = [
        { id: 'home', label: t('pdfEditor.ribbon.tabs.home') },
        { id: 'insert', label: t('pdfEditor.ribbon.tabs.insert') },
        { id: 'design', label: t('pdfEditor.ribbon.tabs.design') },
        { id: 'layout', label: t('pdfEditor.ribbon.tabs.layout') },
        { id: 'references', label: t('pdfEditor.ribbon.tabs.references') },
        { id: 'markup', label: t('pdfEditor.ribbon.tabs.markup') },
        { id: 'view', label: t('pdfEditor.ribbon.tabs.view') },
    ];

    const g = (key) => t(`pdfEditor.ribbon.groups.${key}`);

    const panels = (
        <Group label={g('panels')} key="panels">
            <BigTool
                icon={IconLayoutList}
                label={t('pdfEditor.tools.thumbnails')} short={t('pdfEditor.ribbonShort.tools_thumbnails')} hint={t('pdfEditor.ribbonHint.tools_thumbnails')}
                active={showThumbnails}
                onClick={toggleThumbnails}
            />
            <BigTool
                icon={IconAdjustments}
                label={t('pdfEditor.inspector.title')} short={t('pdfEditor.ribbonShort.inspector_title')} hint={t('pdfEditor.ribbonHint.inspector_title')}
                active={showInspector}
                onClick={toggleInspector}
            />
            <BigTool
                icon={IconRuler2}
                label={t('pdfEditor.tools.ruler')} short={t('pdfEditor.ribbonShort.tools_ruler')} hint={t('pdfEditor.ribbonHint.tools_ruler')}
                active={showRuler}
                onClick={toggleRuler}
            />
        </Group>
    );

    const select = (
        <Group label={g('select')} key="select">
            <SmallTool
                icon={IconArrowsMove}
                label={t('pdfEditor.tools.move')}
                active={activeTool === TOOLS.SELECT}
                onClick={() => setTool(TOOLS.SELECT)}
            />
            <SmallTool icon={IconHandStop} label={t('pdfEditor.tools.pan')} {...tool(TOOLS.HAND)} />
        </Group>
    );

    const TAB_CONTENT = {
        home: [
            panels,
            <Group label={g('text')} key="text">
                <BigTool icon={IconTextPlus} label={t('pdfEditor.tools.addText')} short={t('pdfEditor.ribbonShort.tools_addText')} hint={t('pdfEditor.ribbonHint.tools_addText')} {...tool(TOOLS.TEXT)} />
                {/* Edit-text acts on a real PDF's text layer; an AI document has none, so hide it in
                    document mode. With click-to-edit it is always on, so there is nothing to arm. */}
                {!documentMode && !clickToEditText && (
                  <BigTool icon={IconTextRecognition} label={t('pdfEditor.tools.editText')} short={t('pdfEditor.ribbonShort.tools_editText')} hint={t('pdfEditor.ribbonHint.tools_editText')} {...tool(TOOLS.EDIT_TEXT)} />
                )}
                {/* Replace-image + remove-watermark both act on what the source PDF
                    already contains; a generated document has neither, so hide them in
                    document mode. */}
                {!documentMode && (
                  <BigTool icon={IconPhotoEdit} label={t('pdfEditor.tools.replaceImage')} short={t('pdfEditor.ribbonShort.tools_replaceImage')} hint={t('pdfEditor.ribbonHint.tools_replaceImage')} {...tool(TOOLS.REPLACE_IMAGE)} />
                )}
                {!documentMode && (
                  <BigTool
                      icon={IconDropletOff}
                      label={t('pdfEditor.tools.removeWatermark')} short={t('pdfEditor.ribbonShort.tools_removeWatermark')} hint={t('pdfEditor.ribbonHint.tools_removeWatermark')}
                      disabled={removingWatermark}
                      onClick={() => onCommand('removeWatermark')}
                  />
                )}
            </Group>,
            <RibbonTextGroups key="format" Group={Group} groupLabel={g} />,
            // Word keeps case and strike-through in Home → Font; so do these,
            // which act on the text box that is selected.
            <Group label={g('textTools')} key="textTools">
                <SmallTool
                    icon={IconStrikethrough}
                    label={t('pdfEditor.tools.strikeThroughText')}
                    active={!!selectedObject?.strike}
                    disabled={selectedObject?.type !== OBJ.TEXT}
                    onClick={toggleStrikeThrough}
                />
                <Stack>
                    <Row>
                        <SmallTool
                            icon={IconLetterCase}
                            label={t('pdfEditor.tools.upperCase')}
                            disabled={selectedObject?.type !== OBJ.TEXT}
                            onClick={() => changeCase('upper')}
                        />
                        <SmallTool
                            icon={IconAbc}
                            label={t('pdfEditor.tools.lowerCase')}
                            disabled={selectedObject?.type !== OBJ.TEXT}
                            onClick={() => changeCase('lower')}
                        />
                    </Row>
                    <Row>
                        <SmallTool
                            icon={IconTextRecognition}
                            label={t('pdfEditor.tools.titleCase')}
                            disabled={selectedObject?.type !== OBJ.TEXT}
                            onClick={() => changeCase('title')}
                        />
                        <SmallTool
                            icon={IconSparkles}
                            label={t('pdfEditor.tools.textOutline')}
                            active={!!selectedObject?.outline}
                            disabled={selectedObject?.type !== OBJ.TEXT}
                            onClick={() => updateObject(selection.uid, selectedObject.id, { outline: !selectedObject.outline })}
                        />
                        <SmallTool
                            icon={IconShadow}
                            label={t('pdfEditor.tools.textShadow')}
                            active={!!selectedObject?.shadow}
                            disabled={selectedObject?.type !== OBJ.TEXT}
                            onClick={() => updateObject(selection.uid, selectedObject.id, { shadow: !selectedObject.shadow })}
                        />
                    </Row>
                </Stack>
            </Group>,
            <Group label={g('markup')} key="markup">
                <SmallTool icon={IconHighlight} label={t('pdfEditor.tools.highlight')} {...tool(TOOLS.HIGHLIGHT)} />
                {/* With no selection these arm the PDF original-text layer (PDF-only); a
                    document has none, and box underline/strike live in the Format group. */}
                {!documentMode && (
                  <SmallTool
                      icon={IconUnderline}
                      label={t(textSelected ? 'pdfEditor.options.underline' : 'pdfEditor.tools.underline')}
                      {...(textSelected
                          ? { active: !!textSource?.underline, onClick: () => writeText('text', { underline: !textSource?.underline }) }
                          : tool(TOOLS.UNDERLINE))}
                  />
                )}
                {!documentMode && (
                  <SmallTool
                      icon={IconStrikethrough}
                      label={t(textSelected ? 'pdfEditor.tools.strikeThroughText' : 'pdfEditor.tools.strikeout')}
                      {...(textSelected
                          ? { active: !!selectedObject.strike, onClick: toggleStrikeThrough }
                          : tool(TOOLS.STRIKEOUT))}
                  />
                )}
            </Group>,
            select,
        ],
        insert: [
            // The mock's "add content" row: the things you put ON the page,
            // together, rather than an image-and-signature pair with the text
            // box a tab away.
            <Group label={g('content')} key="content">
                <BigTool icon={IconTextPlus} label={t('pdfEditor.tools.addText')} short={t('pdfEditor.ribbonShort.tools_addText')} hint={t('pdfEditor.ribbonHint.tools_addText')} {...tool(TOOLS.TEXT)} />
                <BigTool icon={IconPhoto} label={t('pdfEditor.tools.image')} short={t('pdfEditor.ribbonShort.tools_image')} hint={t('pdfEditor.ribbonHint.tools_image')} onClick={onPickImage} />
                {/* Swaps a picture the PDF already has — a generated document has none. */}
                {!documentMode && (
                  <BigTool
                      icon={IconPhotoEdit}
                      label={t('pdfEditor.tools.replaceImage')} short={t('pdfEditor.ribbonShort.tools_replaceImage')} hint={t('pdfEditor.ribbonHint.tools_replaceImage')}
                      {...tool(TOOLS.REPLACE_IMAGE)}
                  />
                )}
                <BigTool icon={IconSignature} label={t('pdfEditor.tools.sign')} short={t('pdfEditor.ribbonShort.tools_sign')} hint={t('pdfEditor.ribbonHint.tools_sign')} onClick={() => setSignModal(true)} />
                <BigTool icon={IconLink} label={t('pdfEditor.tools.link')} short={t('pdfEditor.ribbonShort.tools_link')} hint={t('pdfEditor.ribbonHint.tools_link')} {...tool(TOOLS.LINK)} />
                <BigTool icon={IconCalendarEvent} label={t('pdfEditor.tools.dateStamp')} short={t('pdfEditor.ribbonShort.tools_dateStamp')} hint={t('pdfEditor.ribbonHint.tools_dateStamp')} onClick={onInsertDate} />
                <BigTool icon={IconMessage2} label={t('pdfEditor.tools.note')} short={t('pdfEditor.ribbonShort.tools_note')} hint={t('pdfEditor.ribbonHint.tools_note')} {...tool(TOOLS.NOTE)} />
                <BigTool icon={IconMoodSmile} label={t('pdfEditor.tools.emoji')} short={t('pdfEditor.ribbonShort.tools_emoji')} hint={t('pdfEditor.ribbonHint.tools_emoji')} onClick={() => setEmojiModal(true)} />
            </Group>,
            // The three that Docs files under Symbols, kept together for the
            // same reason: they are all "a character the keyboard will not type".
            <Group label={g('pagesInsert')} key="pagesInsert">
                <div ref={coverRef} className="flex">
                    <BigTool
                        icon={IconTypography}
                        label={t('pdfEditor.tools.coverPage')} short={t('pdfEditor.ribbonShort.tools_coverPage')} hint={t('pdfEditor.ribbonHint.tools_coverPage')}
                        onClick={() => setOpenMenu(openMenu === 'cover' ? null : 'cover')}
                    />
                </div>
            </Group>,
            <Group label={g('illustrations')} key="illustrations">
                <div ref={chartRef} className="flex">
                    <BigTool
                        icon={IconChartBar}
                        label={t('pdfEditor.tools.chart')} short={t('pdfEditor.ribbonShort.tools_chart')} hint={t('pdfEditor.ribbonHint.tools_chart')}
                        onClick={() => setOpenMenu(openMenu === 'chart' ? null : 'chart')}
                    />
                </div>
                <BigTool icon={IconScreenshot} label={t('pdfEditor.tools.screenshot')} short={t('pdfEditor.ribbonShort.tools_screenshot')} hint={t('pdfEditor.ribbonHint.tools_screenshot')} onClick={onScreenshot} />
            </Group>,
            <Group label={g('fields')} key="fields">
                <Stack>
                    <Row>
                        <div ref={fieldRef} className="flex">
                            <SmallTool
                                icon={IconFileImport}
                                label={t('pdfEditor.tools.field')}
                                onClick={() => setOpenMenu(openMenu === 'field' ? null : 'field')}
                            />
                        </div>
                    </Row>
                    <Row>
                        <SmallTool
                            icon={IconLetterA}
                            label={t('pdfEditor.tools.dropCap')}
                            disabled={selectedObject?.type !== OBJ.TEXT}
                            onClick={onDropCap}
                        />
                    </Row>
                </Stack>
            </Group>,
            <Group label={g('tables')} key="tables">
                <div ref={tableRef} className="flex">
                    <BigTool
                        icon={IconTable}
                        label={t('pdfEditor.tools.table')} short={t('pdfEditor.ribbonShort.tools_table')} hint={t('pdfEditor.ribbonHint.tools_table')}
                        onClick={() => setOpenMenu(openMenu === 'table' ? null : 'table')}
                    />
                </div>
            </Group>,
            <Group label={g('headerFooter')} key="headerFooter">
                <Stack>
                    <Row>
                        <div ref={headerRef} className="flex">
                            <SmallTool
                                icon={IconLayoutNavbar}
                                label={t('pdfEditor.tools.header')}
                                active={isOn(HEADER_ROLE)}
                                onClick={() => (isOn(HEADER_ROLE)
                                    ? onCommand('header', { text: null })
                                    : (setStampText(''), setOpenMenu('header')))}
                            />
                        </div>
                        <div ref={footerRef} className="flex">
                            <SmallTool
                                icon={IconLayoutBottombar}
                                label={t('pdfEditor.tools.footer')}
                                active={isOn(FOOTER_ROLE)}
                                onClick={() => (isOn(FOOTER_ROLE)
                                    ? onCommand('footer', { text: null })
                                    : (setStampText(''), setOpenMenu('footer')))}
                            />
                        </div>
                    </Row>
                    <Row>
                        <SmallTool
                            icon={IconSquarePlus}
                            label={t('pdfEditor.pages.insertBlank')}
                            onClick={() => insertBlankPage(currentPage)}
                        />
                    </Row>
                </Stack>
            </Group>,
            <Group label={g('symbols')} key="symbols">
                <SmallTool icon={IconAlphabetGreek} label={t('pdfEditor.tools.specialChars')} onClick={() => setSymbolModal(true)} />
                <SmallTool icon={IconMathFunction} label={t('pdfEditor.tools.equation')} onClick={() => setEquationModal(true)} />
            </Group>,
            <Group label={g('shapes')} key="shapes">
                <Stack>
                    <Row>
                        <SmallTool icon={IconRectangle} label={t('pdfEditor.shapes.rect')} {...shape(OBJ.RECT)} />
                        <SmallTool icon={IconCircle} label={t('pdfEditor.shapes.ellipse')} {...shape(OBJ.ELLIPSE)} />
                    </Row>
                    <Row>
                        <SmallTool icon={IconLine} label={t('pdfEditor.shapes.line')} {...shape(OBJ.LINE)} />
                        <SmallTool icon={IconArrowNarrowRight} label={t('pdfEditor.shapes.arrow')} {...shape(OBJ.ARROW)} />
                    </Row>
                </Stack>
            </Group>,
            <Group label={g('marks')} key="marks">
                <SmallTool icon={IconCheckMark} label={t('pdfEditor.tools.check')} {...tool(TOOLS.CHECK)} />
                <SmallTool icon={IconX} label={t('pdfEditor.tools.cross')} {...tool(TOOLS.CROSS)} />
            </Group>,
            <Group label={g('stamps')} key="stamps" last>
                <div ref={numbersRef} className="flex">
                    <BigTool
                        icon={IconListNumbers}
                        label={t('pdfEditor.tools.pageNumbers')} short={t('pdfEditor.ribbonShort.tools_pageNumbers')} hint={t('pdfEditor.ribbonHint.tools_pageNumbers')}
                        active={numbered}
                        onClick={() => setOpenMenu(openMenu === 'numbers' ? null : 'numbers')}
                    />
                </div>
            </Group>,
        ],
        design: [
            <Group label={g('watermark')} key="watermark">
                <div ref={watermarkRef} className="flex">
                    <BigTool
                        icon={IconDroplet}
                        label={t('pdfEditor.tools.watermark')} short={t('pdfEditor.ribbonShort.tools_watermark')} hint={t('pdfEditor.ribbonHint.tools_watermark')}
                        active={isOn(WATERMARK_ROLE)}
                        onClick={() => (isOn(WATERMARK_ROLE)
                            ? onCommand('watermark', { text: null })
                            : (setStampText(''), setOpenMenu('watermark')))}
                    />
                </div>
                {/* Removes the watermark the source FILE arrived with — a generated
                    document has none, so hide it in document mode. */}
                {!documentMode && (
                  <BigTool
                      icon={IconDropletOff}
                      label={t('pdfEditor.tools.removeWatermark')} short={t('pdfEditor.ribbonShort.tools_removeWatermark')} hint={t('pdfEditor.ribbonHint.tools_removeWatermark')}
                      disabled={removingWatermark}
                      onClick={() => onCommand('removeWatermark')}
                  />
                )}
            </Group>,
            <Group label={g('themes')} key="themes">
                <div ref={spacingRef} className="flex">
                    <BigTool
                        icon={IconListNumbers}
                        label={t('pdfEditor.tools.paragraphSpacing')} short={t('pdfEditor.ribbonShort.tools_paragraphSpacing')} hint={t('pdfEditor.ribbonHint.tools_paragraphSpacing')}
                        onClick={() => setOpenMenu(openMenu === 'spacing' ? null : 'spacing')}
                    />
                </div>
            </Group>,
            <Group label={g('pageLook')} key="pageLook" last>
                <div ref={pageBorderRef} className="flex">
                    <BigTool
                        icon={IconBorderAll}
                        label={t('pdfEditor.tools.pageBorder')} short={t('pdfEditor.ribbonShort.tools_pageBorder')} hint={t('pdfEditor.ribbonHint.tools_pageBorder')}
                        active={isOn(PAGE_BORDER_ROLE)}
                        onClick={() => (isOn(PAGE_BORDER_ROLE)
                            ? onCommand('pageBorder', { stroke: null })
                            : setOpenMenu('pageBorder'))}
                    />
                </div>
            </Group>,
        ],
        layout: [
            // Orientation turns the whole document; the single-page rotate is
            // in the page group with the rest of the per-page operations.
            <Group label={g('pageSetup')} key="setup">
                <BigTool
                    icon={IconRotate}
                    label={t('pdfEditor.tools.orientation')} short={t('pdfEditor.ribbonShort.tools_orientation')} hint={t('pdfEditor.ribbonHint.tools_orientation')}
                    onClick={() => rotateAllPages(90)}
                />
                <BigTool icon={IconLayoutColumns} label={t('pdfEditor.tools.managePages')} short={t('pdfEditor.ribbonShort.tools_managePages')} hint={t('pdfEditor.ribbonHint.tools_managePages')} onClick={() => setPagesModal(true)} />
                <BigTool
                    icon={IconLineNumbers}
                    label={t('pdfEditor.tools.lineNumbers')} short={t('pdfEditor.ribbonShort.tools_lineNumbers')} hint={t('pdfEditor.ribbonHint.tools_lineNumbers')}
                    active={isOn('lineNumber')}
                    onClick={() => onCommand('lineNumbers')}
                />
            </Group>,
            // Word's Layout → Page Setup: the paper, its margins, how the text
            // is set on it.
            <Group label={g('paper')} key="paper">
                <div ref={marginsRef} className="flex">
                    <BigTool
                        icon={IconRuler2}
                        label={t('pdfEditor.paper.margins')} short={t('pdfEditor.ribbonShort.paper_margins')} hint={t('pdfEditor.ribbonHint.paper_margins')}
                        active={showMargins}
                        onClick={() => setOpenMenu(openMenu === 'margins' ? null : 'margins')}
                    />
                </div>
                <div ref={sizeRef} className="flex">
                    <BigTool
                        icon={IconFileHorizontal}
                        label={t('pdfEditor.paper.size')} short={t('pdfEditor.ribbonShort.paper_size')} hint={t('pdfEditor.ribbonHint.paper_size')}
                        onClick={() => setOpenMenu(openMenu === 'pageSize' ? null : 'pageSize')}
                    />
                </div>
                <Stack>
                    <Row>
                        <div ref={columnsRef} className="flex">
                            <SmallTool
                                icon={IconColumns}
                                label={t('pdfEditor.columns.title')}
                                disabled={selectedObject?.type !== OBJ.TEXT}
                                onClick={() => setOpenMenu(openMenu === 'columns' ? null : 'columns')}
                            />
                        </div>
                    </Row>
                    <Row>
                        <SmallTool
                            icon={IconAlignJustified}
                            label={t('pdfEditor.paper.hyphenation')}
                            active={hyphenate}
                            onClick={() => onCommand('hyphenation', { on: !hyphenate })}
                        />
                    </Row>
                </Stack>
            </Group>,
            <Group label={g('pages')} key="pages">
                <Stack>
                    <Row>
                        <SmallTool
                            icon={IconSquarePlus}
                            label={t('pdfEditor.pages.insertBlank')}
                            onClick={() => insertBlankPage(currentPage)}
                        />
                        <SmallTool
                            icon={IconCopy}
                            label={t('pdfEditor.pages.duplicate')}
                            onClick={() => duplicatePages([pageUid])}
                        />
                        <SmallTool
                            icon={IconTrash}
                            label={t('pdfEditor.pages.delete')}
                            disabled={onePage}
                            onClick={() => deletePages([pageUid])}
                        />
                    </Row>
                    <Row>
                        <SmallTool
                            icon={IconRotateClockwise}
                            label={t('pdfEditor.pages.rotate')}
                            onClick={() => rotatePage(pageUid, 90)}
                        />
                        <SmallTool
                            icon={IconSort09}
                            label={t('pdfEditor.pages.reorder')}
                            disabled={onePage}
                            onClick={() => setPagesModal(true)}
                        />
                    </Row>
                </Stack>
            </Group>,
            // Word's Layout → Arrange: where a floating object sits on the
            // page, what it sits in front of, and which way up it is.
            <Group label={g('align')} key="align">
                <Stack>
                    <Row>
                        <SmallTool icon={IconLayoutAlignLeft} label={t('pdfEditor.arrange.alignLeft')} disabled={!selectedObject} onClick={() => alignToPage('left')} />
                        <SmallTool icon={IconLayoutAlignCenter} label={t('pdfEditor.arrange.alignCenter')} disabled={!selectedObject} onClick={() => alignToPage('center')} />
                        <SmallTool icon={IconLayoutAlignRight} label={t('pdfEditor.arrange.alignRight')} disabled={!selectedObject} onClick={() => alignToPage('right')} />
                    </Row>
                    <Row>
                        <SmallTool icon={IconLayoutAlignTop} label={t('pdfEditor.arrange.alignTop')} disabled={!selectedObject} onClick={() => alignToPage('top')} />
                        <SmallTool icon={IconLayoutAlignMiddle} label={t('pdfEditor.arrange.alignMiddle')} disabled={!selectedObject} onClick={() => alignToPage('middle')} />
                        <SmallTool icon={IconLayoutAlignBottom} label={t('pdfEditor.arrange.alignBottom')} disabled={!selectedObject} onClick={() => alignToPage('bottom')} />
                    </Row>
                </Stack>
            </Group>,
            <Group label={g('order')} key="order">
                <Stack>
                    <Row>
                        <SmallTool icon={IconArrowBarToUp} label={t('pdfEditor.arrange.toFront')} disabled={!selectedObject} onClick={() => order('front')} />
                        <SmallTool icon={IconChevronUp} label={t('pdfEditor.arrange.forward')} disabled={!selectedObject} onClick={() => order(1)} />
                    </Row>
                    <Row>
                        <SmallTool icon={IconArrowBarToDown} label={t('pdfEditor.arrange.toBack')} disabled={!selectedObject} onClick={() => order('back')} />
                        <SmallTool icon={IconChevronDown} label={t('pdfEditor.arrange.backward')} disabled={!selectedObject} onClick={() => order(-1)} />
                    </Row>
                </Stack>
                <Stack>
                    <Row>
                        <SmallTool icon={IconRotateClockwise} label={t('pdfEditor.arrange.rotateCw')} disabled={!selectedObject} onClick={() => rotateSelection(90)} />
                    </Row>
                    <Row>
                        <SmallTool icon={IconRotate2} label={t('pdfEditor.arrange.rotateCcw')} disabled={!selectedObject} onClick={() => rotateSelection(-90)} />
                    </Row>
                </Stack>
            </Group>,
            <Group label={g('arrange')} key="arrange" last>
                <SmallTool
                    icon={IconLayoutList}
                    label={t('pdfEditor.layout.continuous')}
                    active={viewMode === 'continuous'}
                    onClick={() => setViewMode('continuous')}
                />
                <SmallTool
                    icon={IconLayoutColumns}
                    label={t('pdfEditor.layout.twoUp')}
                    active={viewMode === 'two-up'}
                    onClick={() => setViewMode('two-up')}
                />
            </Group>,
        ],
        // Word's References tab, as a PDF has it: the outline a reader opens
        // down the side, and footnotes and endnotes.
        references: [
            <Group label={g('bookmarksGroup')} key="bookmarksGroup">
                <BigTool icon={IconBookmark} label={t('pdfEditor.bookmarks.add')} short={t('pdfEditor.ribbonShort.bookmarks_add')} hint={t('pdfEditor.ribbonHint.bookmarks_add')} onClick={onAddBookmark} />
                <BigTool
                    icon={IconList}
                    label={t('pdfEditor.bookmarks.panel')} short={t('pdfEditor.ribbonShort.bookmarks_panel')} hint={t('pdfEditor.ribbonHint.bookmarks_panel')}
                    active={showBookmarks}
                    onClick={toggleBookmarks}
                />
            </Group>,
            <Group label={g('footnotes')} key="footnotes" last>
                <BigTool
                    icon={IconNote}
                    label={t('pdfEditor.notes.insertFootnote')} short={t('pdfEditor.ribbonShort.notes_insertFootnote')} hint={t('pdfEditor.ribbonHint.notes_insertFootnote')}
                    onClick={() => onInsertNote('footnote')}
                />
                <BigTool
                    icon={IconNotes}
                    label={t('pdfEditor.notes.insertEndnote')} short={t('pdfEditor.ribbonShort.notes_insertEndnote')} hint={t('pdfEditor.ribbonHint.notes_insertEndnote')}
                    onClick={() => onInsertNote('endnote')}
                />
            </Group>,
        ],
        markup: [
            <Group label={g('draw')} key="draw">
                <BigTool icon={IconPencil} label={t('pdfEditor.tools.pencil')} short={t('pdfEditor.ribbonShort.tools_pencil')} hint={t('pdfEditor.ribbonHint.tools_pencil')} {...tool(TOOLS.PENCIL)} />
                <SmallTool icon={IconHighlight} label={t('pdfEditor.tools.highlight')} {...tool(TOOLS.HIGHLIGHT)} />
            </Group>,
            // The mock calls these Approve and Reject; they are the tick and
            // the cross, which is all an approval mark on a PDF ever is.
            <Group label={g('decide')} key="decide">
                <SmallTool icon={IconCheckMark} label={t('pdfEditor.tools.approve')} {...tool(TOOLS.CHECK)} />
                <SmallTool icon={IconX} label={t('pdfEditor.tools.reject')} {...tool(TOOLS.CROSS)} />
            </Group>,
            // Word's Review → Tracking: record what is being done, and list it.
            <Group label={g('tracking')} key="tracking">
                <BigTool
                    icon={IconHistory}
                    label={t('pdfEditor.review.trackChanges')} short={t('pdfEditor.ribbonShort.review_trackChanges')} hint={t('pdfEditor.ribbonHint.review_trackChanges')}
                    active={trackChanges}
                    onClick={toggleTrackChanges}
                />
                <BigTool
                    icon={IconList}
                    label={t('pdfEditor.review.pane')} short={t('pdfEditor.ribbonShort.review_pane')} hint={t('pdfEditor.ribbonHint.review_pane')}
                    active={showReviewing}
                    onClick={toggleReviewing}
                />
            </Group>,
            <Group label={g('accessibility')} key="accessibility" last>
                <BigTool
                    icon={IconAccessible}
                    label={t('pdfEditor.a11y.check')} short={t('pdfEditor.ribbonShort.a11y_check')} hint={t('pdfEditor.ribbonHint.a11y_check')}
                    active={showAccessibility}
                    onClick={onCheckAccessibility}
                />
                <SmallTool
                    icon={IconList}
                    label={t('pdfEditor.a11y.panel')}
                    active={showAccessibility}
                    onClick={toggleAccessibility}
                />
            </Group>,
        ],
        view: [
            // Word's four views, and its split.
            <Group label={g('views')} key="views">
                <BigTool
                    icon={IconFileText}
                    label={t('pdfEditor.views.print')} short={t('pdfEditor.ribbonShort.views_print')} hint={t('pdfEditor.ribbonHint.views_print')}
                    active={displayMode === 'print'}
                    onClick={() => setDisplayMode('print')}
                />
                <BigTool
                    icon={IconBrowser}
                    label={t('pdfEditor.views.web')} short={t('pdfEditor.ribbonShort.views_web')} hint={t('pdfEditor.ribbonHint.views_web')}
                    active={displayMode === 'web'}
                    onClick={() => setDisplayMode('web')}
                />
                <BigTool
                    icon={IconListTree}
                    label={t('pdfEditor.views.outline')} short={t('pdfEditor.ribbonShort.views_outline')} hint={t('pdfEditor.ribbonHint.views_outline')}
                    active={displayMode === 'outline'}
                    onClick={() => setDisplayMode('outline')}
                />
                <BigTool
                    icon={IconFileDescription}
                    label={t('pdfEditor.views.draft')} short={t('pdfEditor.ribbonShort.views_draft')} hint={t('pdfEditor.ribbonHint.views_draft')}
                    active={displayMode === 'draft'}
                    onClick={() => setDisplayMode('draft')}
                />
            </Group>,
            <Group label={g('window')} key="window">
                <BigTool
                    icon={IconLayoutBoardSplit}
                    label={t('pdfEditor.views.split')} short={t('pdfEditor.ribbonShort.views_split')} hint={t('pdfEditor.ribbonHint.views_split')}
                    active={splitView}
                    disabled={displayMode === 'outline'}
                    onClick={toggleSplitView}
                />
            </Group>,
            panels,
            <Group label={g('show')} key="show">
                <SmallTool
                    icon={IconGridDots}
                    label={t('pdfEditor.tools.gridlines')}
                    active={showGrid}
                    onClick={toggleGrid}
                />
            </Group>,
            <Group label={g('zoom')} key="zoom">
                <SmallTool
                    icon={IconPercentage}
                    label={t('pdfEditor.layout.actualSize')}
                    active={fitMode === 'manual'}
                    onClick={() => { setFitMode('manual'); setZoom(1); }}
                />
                <SmallTool
                    icon={IconWindowMaximize}
                    label={t('pdfEditor.layout.fitWidth')}
                    active={fitMode === 'width'}
                    onClick={() => setFitMode('width')}
                />
                <SmallTool
                    icon={IconZoomScan}
                    label={t('pdfEditor.layout.fitPage')}
                    active={fitMode === 'page'}
                    onClick={() => setFitMode('page')}
                />
            </Group>,
            <Group label={g('arrange')} key="arrange" last>
                <SmallTool
                    icon={IconLayoutColumns}
                    label={t('pdfEditor.layout.twoUp')}
                    active={viewMode === 'two-up'}
                    onClick={() => setViewMode(viewMode === 'two-up' ? 'continuous' : 'two-up')}
                />
            </Group>,
        ],
    };

    return (
        // The ribbon is chrome, not somewhere to put the caret. Without both of
        // these, clicking Bold while a box is open blurs the editor, the blur
        // commits and deselects it, and the press then lands with nothing
        // selected — so it changed the text tool's defaults and appeared to do
        // nothing at all. The options bar has always carried them; the ribbon
        // took over its job and has to carry them too.
        <div
            {...{ [EDITOR_CHROME_ATTR]: '' }}
            onMouseDown={keepTextEditorFocus}
            className="flex-none border-b border-[#ECE8E4] bg-white dark:border-zinc-800 dark:bg-zinc-900"
        >
            {/* Tabs. The active one is underlined rather than filled: it labels
                the row beneath it, it is not a button you press. */}
            {/* On a phone the name and actions fill the first line and the tabs
                wrap onto a line of their own; side by side, the actions would
                squeeze both the name and the tabs to nothing. */}
            <div className={`flex items-stretch gap-x-2 px-3 ${compact ? 'flex-wrap' : 'h-12'}`}>
                {leading ? (
                    <div className={`flex min-w-0 items-center ${compact ? 'h-11 flex-1 basis-0' : 'shrink'}`}>{leading}</div>
                ) : null}
                <div
                    className={`flex min-w-0 gap-0.5 overflow-x-auto ${compact ? 'order-last h-9 w-full items-end' : 'items-stretch'} ${disabled ? 'pointer-events-none select-none opacity-45' : ''}`}
                    aria-hidden={disabled || undefined}
                >
                    {TABS.map((item) => {
                        const on = tab === item.id;
                        return (
                            <button
                                key={item.id}
                                type="button"
                                onClick={() => setTab(item.id)}
                                aria-selected={on}
                                role="tab"
                                // Full row height on desktop, so the label sits on the
                                // same centre line as the name and the action icons
                                // and the underline lands on the ribbon's edge.
                                className={`relative flex-none px-[15px] text-[13.5px] transition-colors ${compact ? 'h-9 rounded-t-lg' : 'h-full'} ${
                                    on
                                        ? 'font-bold text-[#1C1815] dark:text-zinc-100'
                                        : 'font-medium text-[#6B635C] hover:text-[#1C1815] dark:text-zinc-400'
                                }`}
                            >
                                {item.label}
                                {on ? (
                                    <span className="absolute inset-x-[15px] bottom-0 h-[2.5px] rounded-t-sm bg-primary" />
                                ) : null}
                            </button>
                        );
                    })}
                </div>

                {compact && leading ? null : <div className="flex-1" />}

                {trailing ? <div className={`flex flex-none items-center ${compact ? 'h-11' : ''}`}>{trailing}</div> : null}
            </div>

            {/* The groups. `overflow-x-auto` is the floor, not the plan: the tabs
                are what keep the row short enough not to need it. */}
            <div
                ref={groupsRef}
                aria-hidden={disabled || undefined}
                className={`pdf-ribbon-scroll flex items-stretch gap-0 overflow-x-auto overflow-y-hidden bg-[#FBFAF9] px-3 pt-1.5 dark:bg-zinc-950 ${overflowing ? 'h-[124px]' : 'h-[116px]'} ${disabled ? 'pointer-events-none select-none opacity-45' : ''}`}
            >
                {TAB_CONTENT[tab]}
            </div>

            <Popover anchorRef={marginsRef} open={openMenu === 'margins'} onClose={() => setOpenMenu(null)} width={230}>
                {Object.entries(MARGIN_PRESETS).map(([id, preset]) => (
                    <MenuItem
                        key={id}
                        label={t(`pdfEditor.paper.marginPresets.${id}`)}
                        active={marginPreset === id}
                        onClick={() => { setOpenMenu(null); onCommand('margins', { margins: preset, preset: id }); }}
                    />
                ))}
                <MenuItem
                    label={showMargins ? t('pdfEditor.paper.hideGuides') : t('pdfEditor.paper.showGuides')}
                    onClick={() => { setOpenMenu(null); toggleMargins(); }}
                />
            </Popover>

            <Popover anchorRef={sizeRef} open={openMenu === 'pageSize'} onClose={() => setOpenMenu(null)} width={240}>
                {Object.entries(PAGE_SIZES).map(([id, size]) => (
                    <MenuItem
                        key={id}
                        label={t(`pdfEditor.paper.sizes.${id}`)}
                        onClick={() => { setOpenMenu(null); onCommand('pageSize', { size }); }}
                    />
                ))}
                <MenuItem
                    label={t('pdfEditor.paper.landscape')}
                    onClick={() => {
                        setOpenMenu(null);
                        const page = pages[currentPage];
                        if (page) setPageSize({ width: page.width, height: page.height }, { allPages: true, landscape: true });
                    }}
                />
            </Popover>

            <Popover anchorRef={columnsRef} open={openMenu === 'columns'} onClose={() => setOpenMenu(null)} width={200}>
                {[2, 3, 4].map((count) => (
                    <MenuItem
                        key={count}
                        label={t('pdfEditor.columns.count', { count: num(count) })}
                        onClick={() => { setOpenMenu(null); onColumns(count); }}
                    />
                ))}
            </Popover>

            {/* Cover page: the three lines a title page is made of. */}
            <Popover anchorRef={coverRef} open={openMenu === 'cover'} onClose={() => setOpenMenu(null)} width={266}>
                <form
                    className="space-y-1.5 p-2"
                    onSubmit={(e) => {
                        e.preventDefault();
                        setOpenMenu(null);
                        onCoverPage({ ...cover, date: new Date().toLocaleDateString() });
                    }}
                >
                    {['title', 'subtitle', 'author'].map((field) => (
                        <input
                            key={field}
                            value={cover[field]}
                            onChange={(e) => setCover((prev) => ({ ...prev, [field]: e.target.value }))}
                            placeholder={t(`pdfEditor.cover.${field}`)}
                            className="h-8 w-full rounded-lg border border-[#ECE8E4] bg-white px-2.5 text-[12.5px] text-[#1C1815] outline-none focus:border-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                        />
                    ))}
                    <button
                        type="submit"
                        disabled={!cover.title.trim()}
                        className="h-8 w-full rounded-lg bg-primary text-[12.5px] font-semibold text-white transition-colors hover:bg-primary/90 disabled:opacity-45"
                    >
                        {t('common.apply')}
                    </button>
                </form>
            </Popover>

            {/* Chart: the numbers, and which of the three shapes to draw them as. */}
            <Popover anchorRef={chartRef} open={openMenu === 'chart'} onClose={() => setOpenMenu(null)} width={250}>
                <form
                    className="space-y-1.5 p-2"
                    onSubmit={(e) => {
                        e.preventDefault();
                        const parts = chart.data.split(',').map((part) => part.trim()).filter(Boolean);
                        const values = parts.map((part) => Number(part.split(':').pop()) || 0);
                        const labels = parts.map((part, i) => (part.includes(':') ? part.split(':')[0].trim() : String(i + 1)));
                        if (!values.length) return;
                        setOpenMenu(null);
                        onChart({ kind: chart.kind, labels, values });
                    }}
                >
                    <div className="flex gap-1">
                        {[
                            { id: 'bar', icon: IconChartBar },
                            { id: 'line', icon: IconLine },
                            { id: 'pie', icon: IconChartPie },
                        ].map(({ id, icon: Icon }) => (
                            <button
                                key={id}
                                type="button"
                                onClick={() => setChart((prev) => ({ ...prev, kind: id }))}
                                aria-pressed={chart.kind === id}
                                className={`flex h-8 flex-1 items-center justify-center rounded-lg border transition-colors ${
                                    chart.kind === id
                                        ? 'border-primary bg-primary-50 text-primary-700'
                                        : 'border-[#ECE8E4] text-[#6B635C] dark:border-zinc-700 dark:text-zinc-300'
                                }`}
                            >
                                <Icon size={16} stroke={1.8} />
                            </button>
                        ))}
                    </div>
                    {/* "Q1: 20, Q2: 35" — a label is optional, a number is not. */}
                    <input
                        value={chart.data}
                        onChange={(e) => setChart((prev) => ({ ...prev, data: e.target.value }))}
                        placeholder={t('pdfEditor.chart.placeholder')}
                        className="h-8 w-full rounded-lg border border-[#ECE8E4] bg-white px-2.5 text-[12.5px] text-[#1C1815] outline-none focus:border-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                    />
                    <button
                        type="submit"
                        className="h-8 w-full rounded-lg bg-primary text-[12.5px] font-semibold text-white transition-colors hover:bg-primary/90"
                    >
                        {t('common.apply')}
                    </button>
                </form>
            </Popover>

            <Popover anchorRef={fieldRef} open={openMenu === 'field'} onClose={() => setOpenMenu(null)} width={210}>
                {['fileName', 'pageCount', 'pageOf', 'date'].map((kind) => (
                    <MenuItem
                        key={kind}
                        label={t(`pdfEditor.fields.${kind}`)}
                        onClick={() => { setOpenMenu(null); onField(kind); }}
                    />
                ))}
            </Popover>

            <Popover anchorRef={spacingRef} open={openMenu === 'spacing'} onClose={() => setOpenMenu(null)} width={210}>
                {[
                    { id: 'tight', value: 1.15 },
                    { id: 'normal', value: 1.35 },
                    { id: 'relaxed', value: 1.6 },
                    { id: 'double', value: 2 },
                ].map((option) => (
                    <MenuItem
                        key={option.id}
                        label={t(`pdfEditor.spacing.${option.id}`)}
                        onClick={() => { setOpenMenu(null); onCommand('paragraphSpacing', { lineHeight: option.value }); }}
                    />
                ))}
            </Popover>

            {/* Word's insert-table grid: pick the size by pointing at it. */}
            <Popover anchorRef={tableRef} open={openMenu === 'table'} onClose={() => setOpenMenu(null)} width={200}>
                <div className="px-1.5 pb-1 pt-0.5 text-[11px] font-semibold text-[#B8B1AA]">
                    {t('pdfEditor.tools.tableSize')}
                </div>
                <div className="grid grid-cols-5 gap-1 p-1.5">
                    {Array.from({ length: 20 }, (_, i) => {
                        const rows = Math.floor(i / 5) + 1;
                        const cols = (i % 5) + 1;
                        return (
                            <button
                                key={`${rows}x${cols}`}
                                type="button"
                                title={`${rows} × ${cols}`}
                                onClick={() => { setOpenMenu(null); onInsertTable(rows, cols); }}
                                className="h-7 rounded-[6px] border border-[#ECE8E4] text-[10px] font-semibold text-[#6B635C] transition-colors hover:border-primary hover:bg-primary-50 hover:text-primary-700 dark:border-zinc-700 dark:text-zinc-300"
                            >
                                {rows}×{cols}
                            </button>
                        );
                    })}
                </div>
            </Popover>

            {/* One text prompt, three switches: the watermark and the two
                running lines all need a line of text and nothing else. */}
            {['watermark', 'header', 'footer'].map((kind) => (
                <Popover
                    key={kind}
                    anchorRef={kind === 'watermark' ? watermarkRef : kind === 'header' ? headerRef : footerRef}
                    open={openMenu === kind}
                    onClose={() => setOpenMenu(null)}
                    width={260}
                >
                    <form
                        className="p-2"
                        onSubmit={(e) => {
                            e.preventDefault();
                            setOpenMenu(null);
                            if (kind === 'watermark') onCommand('watermark', { text: stampText });
                            else onCommand(kind, { text: stampText });
                        }}
                    >
                        <label className="mb-1 block text-[11px] font-semibold text-[#B8B1AA]">
                            {t(`pdfEditor.tools.${kind}Text`)}
                        </label>
                        <input
                            value={stampText}
                            onChange={(e) => setStampText(e.target.value)}
                            autoFocus
                            placeholder={t(`pdfEditor.tools.${kind}Placeholder`)}
                            className="h-8 w-full rounded-lg border border-[#ECE8E4] bg-white px-2.5 text-[12.5px] text-[#1C1815] outline-none focus:border-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                        />
                        <button
                            type="submit"
                            disabled={!stampText.trim()}
                            className="mt-2 h-8 w-full rounded-lg bg-primary text-[12.5px] font-semibold text-white transition-colors hover:bg-primary/90 disabled:opacity-45"
                        >
                            {t('common.apply')}
                        </button>
                        {/* A watermark is as often a logo as a word. The two
                            are one switch: picking an image replaces whatever
                            is there, and the button still takes it off. */}
                        {kind === 'watermark' ? (
                            <button
                                type="button"
                                onClick={() => { setOpenMenu(null); onWatermarkImage?.(); }}
                                className="mt-1.5 inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-lg border border-[#ECE8E4] bg-white text-[12.5px] font-semibold text-[#3D3833] transition-colors hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                            >
                                <IconPhotoPlus size={14} stroke={1.8} />
                                {t('pdfEditor.tools.watermarkImage')}
                            </button>
                        ) : null}
                    </form>
                </Popover>
            ))}

            {/* Page border: a colour is all it needs. */}
            {[
                { kind: 'pageBorder', anchor: pageBorderRef, swatches: ['#1C1815', '#6B635C', '#C24A2E', '#1E5FA8', '#1E7A4B', '#8A6A2F'] },
            ].map(({ kind, anchor: ref, swatches }) => (
                <Popover key={kind} anchorRef={ref} open={openMenu === kind} onClose={() => setOpenMenu(null)} width={196}>
                    <div className="px-1.5 pb-1 pt-0.5 text-[11px] font-semibold text-[#B8B1AA]">
                        {t(`pdfEditor.tools.${kind}`)}
                    </div>
                    <div className="grid grid-cols-6 gap-1.5 p-1.5">
                        {swatches.map((colour) => (
                            <button
                                key={colour}
                                type="button"
                                title={colour}
                                onClick={() => { setOpenMenu(null); onCommand(kind, { stroke: colour }); }}
                                style={{ background: colour }}
                                className="h-7 w-7 rounded-[7px] border border-[#DAD5CE] transition-transform hover:scale-110 dark:border-zinc-600"
                            />
                        ))}
                    </div>
                </Popover>
            ))}

            <Popover anchorRef={numbersRef} open={openMenu === 'numbers'} onClose={() => setOpenMenu(null)} width={230}>
                <MenuItem
                    label={t('pdfEditor.tools.pageNumbersBn')}
                    active={numberDigits === 'bn'}
                    onClick={() => { setOpenMenu(null); onCommand('pageNumbers', { digits: 'bn' }); }}
                />
                <MenuItem
                    label={t('pdfEditor.tools.pageNumbersEn')}
                    active={numberDigits === 'en'}
                    onClick={() => { setOpenMenu(null); onCommand('pageNumbers', { digits: 'en' }); }}
                />
            </Popover>
        </div>
    );
}
